import { Routes, Route, Navigate, useParams } from "react-router-dom";
import useUser from "./hooks/user";
import LoginPage from "./Pages/Login";
import Demo from "./Pages/Calendar";
import PatientArchive from "./Pages/Patient/PatientArchive";
import PatientCase from "./Pages/Patient/PatientCase";
import PatientInfo from "./Pages/Patient/PatientInfo";
import PatientAppointments from "./Pages/Patient/PatientAppointments";
import UpdateAppointment from "./Pages/Patient/UpdateAppointment";
import CreateAppointment from "./Pages/Patient/CreateAppointment";
import PatientMessages from "./Pages/Patient/PatientMessages";
import PatientPhotos from "./Pages/Patient/PatientPhotos";
import GalleryView from "./Pages/Patient/GalleryView";
import PatientExternalMedia from "./Pages/Patient/PatientExternalMedia";
import ExternalMediaGalleryView from "./Pages/Patient/ExternalMediaGalleryView";
import ExternalMediaPatientCase from "./Pages/Patient/ExternalMediaPatientCase";
import Clinic from "./Pages/Clinic/Clinic";
import NewDashboard from "./Pages/NewDashboard";
import PatientActivity from "./Pages/Patient/PatientActivity";
import StaffList from "./Pages/Staff/StaffList";
import ResetPassword from "./components/ResetPassword";
import ForgotPassword from "./components/forgotPassword";
import Reward from "./Pages/Patient/Rewards";
import AdHocReport from "./Pages/activity/AdHocReport";
import FullForm from "./Pages/Ecrf/FullForm";
import TrialsList from "./Pages/Trials/TrialsList";
import TrialDetails from "./Pages/Trials/TrialsDetails";
import TrialLayout from "./Pages/Trials/TrialLayout";
import TrialHierarchyTab from "./Pages/Trials/TrialHierarchyTab";
import TrialDocumentsTab from "./Pages/Trials/TrialDocumentsTab";
import PoemList from "./Pages/Poem/PoemList";
import PoemDetail from "./Pages/Poem/PoemDetail";
import EConsent from "./Pages/Poem/eConsent";
import ChangeLogs from "./Pages/Poem/ecrfChangeLogs";
import VisitConfigLogs from "./Pages/Patient/VisitConfigLogs";
import ProofVideos from "./Pages/Poem/proofVideos";
import VideoDetail from "./Pages/Poem/VideoDetail";
import NotificationList from "./Pages/Notifications/notification_list";
import StaffNotificationPage from "./Pages/Notifications/StaffNotificationPage";
import "react-toastify/dist/ReactToastify.css";
import { LicenseInfo } from "@mui/x-license";
import PatientExternalDocuments from "./Pages/Patient/PatientExternalDocuments";
import PatientHistograms from "./Pages/Patient/PatientHistograms";
import ExternalDocumentsCase from "./Pages/Patient/ExternalDocumentsCase";
import SurveyPoem from "./components/Survey/SurveyPoem";
import SummaryTrial from "./Pages/Trials/SummaryTrial";
import SurveyCreatorWidget from "./Pages/FormCreater/SurveyCreatorReact";
import SurveyListWidget from "./Pages/FormCreater/SurveyList";
import ManuallyMarkVisit from "./components/TrialAccordions/ManuallyMarkVisit";
import { setLicenseKey, slk } from "survey-core";
import { initSurveyLocalization } from "./utils/surveyLocalization";
import QueryTable from "../src/components/Query/QueryTable";
import QueryChangeLogs from "./components/Query/QueryChangeLog";
import DeleteAccount from "./Pages/DeleteAccount";
import UserActionLogs from "./Pages/UserActionLogs";
import SingleUserLogs from "./Pages/SingleUserLogs";
import PatientLayout from "./Pages/Patient/PatientLayout";
import { EcrfProvider } from "./context/EcrfContext";
import EconsentDetails from "./Pages/Poem/EconsentDetails";
import ExternalMediaLogs from "./Pages/Patient/ExternalMediaLogs";
import AdvancedSurveys from "./Pages/Poem/AdvancedSurveys";
import AdvancedSurveysCreator from "./Pages/Poem/AdvancedSurveysCreator";
import AdvancedSurveyWebView from "./Pages/WebView/AdvancedSurveyWebView";
import SurveyHistogramWebView from "./Pages/WebView/SurveyHistogramWebView";
import PatientAdvancedSurveys from "./Pages/Poem/PatientAdvancedSurveys";
import PatientAdvancedSurveysDetails from "./Pages/Poem/PatientAdvancedSurveysDetails";
import PatientEdiary from "./Pages/Patient/PatientEdiary";
import PatientAppDocuments from "./Pages/Patient/PatientAppDocuments";
import SupportList from "./Pages/Support/SupportList";
import SupportDetail from "./Pages/Support/SupportDetail";
import { CRO_RELATED_ROLES, INV_RELATED_ROLES, USER_ROLES } from "./utils/constants";

LicenseInfo.setLicenseKey(process.env.REACT_APP_MUI_LICENSE_KEY);
setLicenseKey(process.env.REACT_APP_SURVEY_PDF_LICENSE_KEY);
slk(process.env.REACT_APP_SURVEY_PDF_LICENSE_KEY);
initSurveyLocalization();

const RedirectToTrialSummary = () => {
  const { _id_ } = useParams();
  return <Navigate to={`/clinic_trials/${_id_}/summary`} replace />;
};

const App = () => {
  const user = useUser();
  const user_data = JSON.parse(localStorage.getItem("user_data"));
  return (
    <Routes>
      <Route
        path="/webview/sessions/:sessionId/advanced-surveys/:advancedSurveyId"
        element={<AdvancedSurveyWebView />}
      />
      <Route
        path="/webview/trials/:trialId/patients/:patientId/histogram"
        element={<SurveyHistogramWebView />}
      />

      {user.isLogin ? (
        <>
          {user_data.role === USER_ROLES.PATIENT && (
            <Route exact path="/" element={<DeleteAccount />} />
          )}

          {(user_data.role === USER_ROLES.REVIEWER ||
            user_data.role === USER_ROLES.CRO ||
            user_data.role === USER_ROLES.CRO_COORDINATOR ||
            user_data.role === USER_ROLES.DATA_MANAGER) && (
            <Route exact path="/" element={<TrialsList />} />
          )}

          {(user_data.role === USER_ROLES.CRC ||
            user_data.role === USER_ROLES.INVESTIGATOR ||
            user_data.role === USER_ROLES.SUB_INVESTIGATOR ||
            user_data.role === USER_ROLES.SUPER_ADMIN ||
            user_data.role === USER_ROLES.CLINIC_ADMIN ||
            user_data.role === USER_ROLES.CLINIC_OWNER ||
            user_data.role === USER_ROLES.PRACTITIONER) && (
            <>
              <Route exact path="/" element={<NewDashboard />} />
            </>
          )}

          {user_data.role !== USER_ROLES.CRC && <Route exact path="/calendar" element={<Demo />} />}

          {user_data.role === USER_ROLES.SUPER_ADMIN ||
          user_data.role === USER_ROLES.CRO ||
          user_data.role === USER_ROLES.CRO_COORDINATOR ? (
            <>
              <Route exact path="/survey-poem" element={<SurveyPoem />} />
              <Route exact path="/form-creater-v2-list" element={<SurveyListWidget />} />
              <Route exact path="/form-creater-v2" element={<SurveyCreatorWidget />} />
            </>
          ) : (
            ""
          )}

          {user_data.role === USER_ROLES.SUPER_ADMIN || user_data.role === USER_ROLES.CRO ? (
            <>
              <Route exact path="/advanced-surveys" element={<AdvancedSurveys />} />

              <Route exact path="/advanced-surveys-creator" element={<AdvancedSurveysCreator />} />
            </>
          ) : (
            ""
          )}

          {!CRO_RELATED_ROLES.includes(user_data.role) && (
            <>
              <Route exact path="/patient/" element={<NewDashboard />} />
              <Route exact path="/patient/archive" element={<PatientArchive />} />
              {user_data.role !== USER_ROLES.CRC && (
                <Route exact path="/patient/adhoc-report" element={<AdHocReport />} />
              )}
              <Route exact path="/patient/user-action-logs" element={<UserActionLogs />} />
              <Route exact path="/patient/single-user-logs" element={<SingleUserLogs />} />

              <Route path="/patient/:_id_" element={<PatientLayout />}>
                {/* Personal Info Tab */}
                <Route path="info" element={<PatientInfo />} />

                {/* Appointments Tab */}
                <Route path="appointment" element={<PatientAppointments />} />
                <Route path="appointment/:UpdateId" element={<UpdateAppointment />} />
                <Route path="appointment/create" element={<CreateAppointment />} />

                {/* Messages Tab */}
                <Route path="messages" element={<PatientMessages />} />

                {/* App Documents Tab */}
                <Route path="app-documents" element={<PatientAppDocuments />} />

                {/* Photos Tab */}
                <Route path="photos" element={<PatientPhotos />} />
                <Route path="photos/:caseId" element={<PatientCase />} />
                <Route path="gallery" element={<GalleryView />} />

                {/* Videos Tab */}
                <Route path="videos/" element={<ProofVideos />} />
                <Route path="video/:caseId" element={<VideoDetail />} />

                {/* External Photos / External Videos Tabs */}
                {(user_data.role === USER_ROLES.CRC ||
                  user_data.role === USER_ROLES.SUPER_ADMIN ||
                  user_data.role === USER_ROLES.INVESTIGATOR ||
                  user_data.role === USER_ROLES.SUB_INVESTIGATOR ||
                  user_data.role === USER_ROLES.CRO ||
                  user_data.role === USER_ROLES.CRO_COORDINATOR ||
                  user_data.role === USER_ROLES.DATA_MANAGER ||
                  user_data.role === USER_ROLES.REVIEWER) && (
                  <>
                    <Route
                      path="sessions_external_photos"
                      element={<PatientExternalMedia mediaType="photo" />}
                    />
                    <Route
                      path="sessions_external_photos/:caseId"
                      element={<ExternalMediaPatientCase mediaType="photo" />}
                    />
                    <Route
                      path="sessions_external_photos_gallery"
                      element={<ExternalMediaGalleryView mediaType="photo" />}
                    />

                    <Route
                      path="sessions_external_videos"
                      element={<PatientExternalMedia mediaType="video" />}
                    />
                    <Route
                      path="sessions_external_videos/:caseId"
                      element={<ExternalMediaPatientCase mediaType="video" />}
                    />
                    <Route
                      path="sessions_external_videos_gallery"
                      element={<ExternalMediaGalleryView mediaType="video" />}
                    />

                    <Route
                      path="external_photo_logs/"
                      element={<ExternalMediaLogs mediaType="photo" />}
                    />
                    <Route
                      path="external_video_logs/"
                      element={<ExternalMediaLogs mediaType="video" />}
                    />
                  </>
                )}

                {/* Histogram Tab */}
                {(user_data.role === USER_ROLES.CRC ||
                  user_data.role === USER_ROLES.SUPER_ADMIN ||
                  user_data.role === USER_ROLES.INVESTIGATOR ||
                  user_data.role === USER_ROLES.SUB_INVESTIGATOR) && (
                  <>
                    <Route path="histograms/" element={<PatientHistograms />} />
                  </>
                )}

                {/* External Documents Tab */}
                <Route path="documents" element={<PatientExternalDocuments />} />
                <Route path="documents/:caseId" element={<ExternalDocumentsCase />} />

                {/* eCrf Tab */}
                <Route
                  path="ecrf/"
                  element={
                    <EcrfProvider>
                      <FullForm />
                    </EcrfProvider>
                  }
                />

                {/* eCRF Change Logs Tab */}
                {user_data.role !== USER_ROLES.REVIEWER && (
                  <Route path="ecrf_change_logs/" element={<ChangeLogs />} />
                )}

                {/* Visit Config Logs Tab */}
                {user_data.role !== USER_ROLES.REVIEWER && (
                  <Route path="visit_config_logs/" element={<VisitConfigLogs />} />
                )}

                {/* Query/eSignature/Freeze Tab */}
                {(user_data.role === USER_ROLES.SUPER_ADMIN ||
                  user_data.role === USER_ROLES.CRO ||
                  user_data.role === USER_ROLES.CRO_COORDINATOR ||
                  user_data.role === USER_ROLES.INVESTIGATOR ||
                  user_data.role === USER_ROLES.CRC ||
                  user_data.role === USER_ROLES.SUB_INVESTIGATOR ||
                  user_data.role === USER_ROLES.DATA_MANAGER) && (
                  <Route path="query/" element={<QueryTable />} />
                )}

                {/* Query Change Logs Tab */}
                <Route path="query_change_logs/" element={<QueryChangeLogs />} />

                {/* Auto Notifications Log Tab */}
                <Route path="notification_list/" element={<NotificationList />} />

                {/* POEM/ePRO/eDiary Tab */}
                <Route path="poem/" element={<PoemList />} />
                <Route path="poem/:caseId" element={<PoemDetail />} />

                {/* Advanced Surveys Tab */}
                <Route path="advanced-surveys/" element={<PatientAdvancedSurveys />} />
                <Route
                  path="advanced-surveys/:sessionId"
                  element={<PatientAdvancedSurveysDetails />}
                />

                {/* eDiary Tab */}
                <Route path="ediary/" element={<PatientEdiary />} />
                <Route
                  path="ediary/:sessionId"
                  element={<PatientAdvancedSurveysDetails isEdiary />}
                />

                {/* eConsent Tab */}
                <Route path="e_consent/" element={<EConsent />} />
                <Route path="e_consent/:caseId" element={<EconsentDetails />} />

                {/* Activity Tab */}
                {user_data.role === USER_ROLES.SUPER_ADMIN && (
                  <Route path="activity" element={<PatientActivity />} />
                )}

                {/* Visit Status Tab */}
                <Route path="visit_status" element={<ManuallyMarkVisit />} />

                {/* Reward Tab */}
                <Route path="reward" element={<Reward />} />
              </Route>
            </>
          )}

          <Route exact path="/clinic_trials/" element={<TrialsList />} />
          <Route path="/clinic_trials/:_id_" element={<TrialLayout />}>
            {(user_data.role === USER_ROLES.SUPER_ADMIN ||
              user_data.role === USER_ROLES.CRO ||
              user_data.role === USER_ROLES.CRO_COORDINATOR ||
              user_data.role === USER_ROLES.DATA_MANAGER ||
              user_data.role === USER_ROLES.CRC ||
              user_data.role === USER_ROLES.INVESTIGATOR ||
              user_data.role === USER_ROLES.SUB_INVESTIGATOR ||
              user_data.role === USER_ROLES.REVIEWER) && (
              <Route path="summary" element={<SummaryTrial />} />
            )}

            {user_data.role === USER_ROLES.SUPER_ADMIN && (
              <Route path="hierarchy" element={<TrialHierarchyTab />} />
            )}

            {(CRO_RELATED_ROLES.includes(user_data.role) ||
              user_data.role === USER_ROLES.SUPER_ADMIN ||
              user_data.role === USER_ROLES.REVIEWER) && (
              <Route
                path="configuration"
                element={
                  <EcrfProvider>
                    <TrialDetails />
                  </EcrfProvider>
                }
              />
            )}

            {user_data.role === USER_ROLES.SUPER_ADMIN && (
              <Route path="documents" element={<TrialDocumentsTab />} />
            )}
          </Route>

          <Route path="/summary_trial/:_id_" element={<RedirectToTrialSummary />} />

          {user_data.role !== USER_ROLES.PRACTITIONER && (
            <Route exact path="/staff/" element={<StaffList />} />
          )}
          {(user_data.role === USER_ROLES.SUPER_ADMIN ||
            user_data.role === USER_ROLES.CLINIC_OWNER ||
            user_data.role === USER_ROLES.INVESTIGATOR ||
            user_data.role === USER_ROLES.SUB_INVESTIGATOR) && (
            <Route exact path="/clinic" element={<Clinic />} />
          )}
          <Route exact path="/notifications" element={<StaffNotificationPage />} />

          {(user_data.role === USER_ROLES.SUPER_ADMIN ||
            INV_RELATED_ROLES.includes(user_data.role)) && (
            <>
              <Route exact path="/support" element={<SupportList />} />
              <Route exact path="/support/:id" element={<SupportDetail />} />
            </>
          )}
        </>
      ) : (
        <>
          <Route path="*" element={<LoginPage />} />
        </>
      )}

      {!user.isLogin ? (
        <>
          <Route exact path="/login" element={<LoginPage />} />
          <Route exact path="/forgot_password" element={<ForgotPassword />} />
          <Route path="auth/password/reset/:uid/:token" element={<ResetPassword />} />
        </>
      ) : (
        <Route path="*" element={<Navigate to="/" />} />
      )}
    </Routes>
  );
};

export default App;
